import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/housing-living-cost";
const TITLE = "Housing & Living Cost 2026 | LifeWestWay";

export const Route = createFileRoute("/housing-living-cost")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("Rent, deposits, bills and monthly budgets for newcomers in the USA, UK, Australia, Canada, Italy and Portugal — real 2026 housing and living cost numbers."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("City-by-city rent, utilities and monthly budget benchmarks for expats across six Western destinations, updated for 2026."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: HousingLivingCostHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  href: string;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "Renting in the USA 2026: Security Deposits, Utility Bills and Health Cover",
    blurb:
      "Average rent, state-level deposit caps, deposit alternatives, renters insurance, utilities and health cover for newcomers.",
    highlight: "$4,500 typical move-in cost",
    href: "/usa/usa-housing-living-costs-rent-deposits-insurance-2026",
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Cost of Renting in the UK 2026: Deposit Caps, Bills and the Health Surcharge",
    blurb:
      "City rent bands, the 5-week deposit cap, government-approved protection schemes, council tax, bills and the NHS surcharge.",
    highlight: "5-week deposit cap + IHS",
    href: "/uk/uk-housing-living-costs-rent-deposits-insurance-2026",
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "Australia Housing & Living Costs: Rent, Bonds, and Health Cover (2026)",
    blurb:
      "How rental bonds are held by state authorities, setup and utility budgets, plus the crucial OSHC vs OVHC health cover split.",
    highlight: "4-week bond + OSHC/OVHC",
    href: "/australia/australia-housing-living-costs-rent-bonds-health-cover-2026",
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "Renting in Canada 2026: Provincial Deposit Rules, Bills and Health Coverage Waits",
    blurb:
      "Province-by-province deposit rules, CMHC rent averages, and the up-to-3-month healthcare waiting period newcomers must insure.",
    highlight: "3-month health gap",
    href: "/canada/canada-housing-living-costs-rent-deposits-health-insurance-2026",
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "Renting in Italy 2026: Caparra Deposits, Agency Fees and the North–South Gap",
    blurb:
      "The caparra deposit, agency fees plus VAT, North-South rent gaps and the private health cover Italian visas require.",
    highlight: "Caparra + codice fiscale",
    href: "/italy/italy-housing-living-costs-rent-deposits-health-insurance-2026",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Renting in Portugal 2026: Lisbon vs Algarve Rents, NIF and SNS Costs",
    blurb:
      "Lisbon vs Algarve rent, one to two months' deposit, the NIF requirement, and SNS vs private health cover.",
    highlight: "NIF + €40–€100/mo cover",
    href: "/portugal/portugal-housing-living-costs-rent-deposits-health-insurance-2026",
  },
];

function HousingLivingCostHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Housing & Living Cost" },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Housing & <span className="text-accent">Living Cost</span> for Expats
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            Rent bands, deposits, utility bills and realistic monthly budgets across all six
            Western destinations — so you know exactly what your first year costs.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Housing and living cost guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the housing and living cost guide: ${g.title}`}
            key={g.title}
            to={g.href}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Explore <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["housing-living-cost"]} />

      <ShareBar />
    </main>
  );
}
